import React, { useEffect, useState } from 'react';
import moment from 'moment';
import CurrencyInput from 'react-currency-input-field';
import { AnswerTypeVariable, AnswerTypeVariableNamesList } from '../../../models/DataRequestHub/ProjectVariable';
import { VendorContractCell, VendorContractColumn } from '../../../models/FlowOfFunds/flow-of-funds.model';
import {
    accountingFormatName,
    formatDataTableCellDisplayValue,
    storedDateFormats,
} from '../../../utils/dataTableCellDisplayValue';
import { PayoffTableDatePicker } from './PayoffTableDatePicker';

const NUMBER_ANSWER_TYPE = AnswerTypeVariableNamesList.find((a) => a.id === AnswerTypeVariable.Number).name;
const DATE_ANSWER_TYPE = AnswerTypeVariableNamesList.find((a) => a.id === AnswerTypeVariable.Date).name;
const DATE_INPUT_FORMAT = 'YYYY-MM-DD';

const toDisplayValue = (isDate: boolean, rawValue: string): string => {
    if (!isDate) {
        return rawValue;
    }
    const parsedDate = rawValue ? moment(rawValue, storedDateFormats) : null;
    return parsedDate?.isValid() ? parsedDate.format(DATE_INPUT_FORMAT) : '';
};

export interface DaqiCellEditorProps {
    column: VendorContractColumn;
    cell: VendorContractCell | undefined;
    isEditable: boolean;
    isRowDirty: boolean;
    onCommit: (nextAnswerText: string) => void;
}

export const DaqiCellEditor = ({ column, cell, isEditable, isRowDirty, onCommit }: DaqiCellEditorProps) => {
    const rawValue = cell?.answerText ?? '';
    const isDate = column.answerType === DATE_ANSWER_TYPE;
    const isPickList = column.options.length > 0;
    const [value, setValue] = useState(() => toDisplayValue(isDate, rawValue));

    useEffect(() => {
        if (!isRowDirty) {
            setValue(toDisplayValue(isDate, rawValue));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rawValue, isRowDirty]);

    if (!isEditable) {
        const displayValue = formatDataTableCellDisplayValue(
            { answerType: column.answerType, formType: column.formType ?? '' },
            rawValue
        );
        return <span>{displayValue}</span>;
    }

    const commit = (nextAnswerText: string) => {
        onCommit(nextAnswerText);
    };

    if (isPickList) {
        return (
            <select
                className="form-control"
                value={value}
                aria-label={column.name}
                onChange={(e) => {
                    setValue(e.target.value);
                    commit(e.target.value);
                }}>
                <option value="" />
                {column.options.map((option) => (
                    <option key={option} value={option}>
                        {option}
                    </option>
                ))}
            </select>
        );
    }

    if (isDate) {
        const selectedDate = value ? moment(value, DATE_INPUT_FORMAT) : null;
        return (
            <PayoffTableDatePicker
                label={column.name}
                selected={selectedDate?.isValid() ? selectedDate.toDate() : null}
                onChange={(nextDate) => {
                    setValue(nextDate ? moment(nextDate).format(DATE_INPUT_FORMAT) : '');
                    commit(nextDate ? nextDate.toDateString() : '');
                }}
            />
        );
    }

    if (column.answerType === NUMBER_ANSWER_TYPE) {
        const isMoneyColumn = column.formType === accountingFormatName;
        if (isMoneyColumn) {
            return (
                <CurrencyInput
                    className="form-control"
                    value={value}
                    allowDecimals
                    decimalsLimit={2}
                    allowNegativeValue={false}
                    prefix="$"
                    groupSeparator=","
                    decimalSeparator="."
                    onValueChange={(nextValue) => setValue(nextValue ?? '')}
                    onBlur={() => commit(value)}
                />
            );
        }
        return (
            <input
                type="number"
                className="form-control"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onBlur={() => commit(value)}
            />
        );
    }

    return (
        <input
            type="text"
            className="form-control"
            value={value}
            aria-label={column.name}
            onChange={(e) => setValue(e.target.value)}
            onBlur={() => commit(value)}
        />
    );
};
